/* eslint-disable @typescript-eslint/naming-convention */
import { Avatar, Box, Grid } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CollapsibleFilterCard from "../../components/CollapsibleFilterCard/CollapsibleFilterCard";
import CustomButton, {
  CustomButtonVariant,
} from "../../components/CustomButton/CustomButton";
import CustomCard from "../../components/CustomCard/CustomCard";
import CustomTabs from "../../components/CustomTabs/CustomTabs";
import Layout from "../../components/Layout/Layout";
import QueryPageTitleCard from "../../components/QueryPageTitleCard/QueryPageTitleCard";
import { routesConfig } from "../../config/routes";
import { getFormattedTimestamp } from "../../utils/dateFormatter";
import { useAuth } from "../../contexts/authentication/AuthenticationContext";
import { Roles } from "../../components/SideBar/Content/ListItem";
import { TypesPermissions } from "../../components/Routes/TypesPermissions";
import { AuthenticationContextData } from "../../contexts/authentication/domain";
import { sidebar } from "../../utils/featureFlags";
import { useFlags } from "flagsmith/react";
import { commonFilter } from "./models/SearchFilterOptions";
import SacFilter from "./Sections/SacFilter";
import OpenCallsTab from "./Sections/OpenCallsTab";
import { SacRegisterQuery } from "../../modules/information/models/SacRegisterQuery";
import { useSac } from "../../contexts/sac/sacContext";
import AwaitingConfirmationCallsTab from "./Sections/AwaitingConfirmationCallsTab";
import ClosedCallsTab from "./Sections/ClosedCallsTab";

const SacPage: React.FC = () => {
  const flags = useFlags(sidebar);
  const { state } = useLocation();
  const [currentTab, setCurrentTab] = useState(0);
  const [query, setQuery] = useState<SacRegisterQuery | null>(null);
  const { authByPermission, permissionSet, roles } =
    useAuth() as AuthenticationContextData;
  const navigate = useNavigate();
  const { registersTotal } = useSac();

  const tabsLabel = useMemo(() => {
    const { closed, open, waiting_confirmation } = registersTotal;

    return [
      `Atendimentos em aberto ${open !== 0 ? `(${open})` : ""}`,
      `Atendimentos em andamento ${
        waiting_confirmation !== 0 ? `(${waiting_confirmation})` : ""
      }`,
      `Atendimentos finalizados ${closed !== 0 ? `(${closed})` : ""}`,
    ];
  }, [registersTotal]);

  const handleDebounce = useCallback(
    (e: any): any => {
      setQuery({
        ...commonFilter,
        ...e,
      });
    },
    [setQuery, currentTab]
  );

  useEffect(() => {
    if (state === null) return;
    setCurrentTab(state.tab);
    navigate(routesConfig.INFORMATION().SAC, {});
  }, [state]);

  const isAllowed = useMemo(() => {
    return (
      authByPermission(
        permissionSet.SYSTEM_MODULES,
        roles.has(Roles.ADMIN),
        TypesPermissions.REQUEST
      ) && flags.supply_new_quote.enabled
    );
  }, [authByPermission, flags]);

  return (
    <Layout
      enableMargin
      appBarProps={{
        notification: {
          onChange() {},
        },
        chat: {
          onChange() {},
        },
        settings: {
          onChange() {},
        },
        avatar: () => (
          <Avatar
            alt="Avatar"
            src="/images/avatar.png"
            sx={{
              width: 60,
              height: 60,
            }}
          />
        ),
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box sx={{ flexShrink: 0, mb: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <QueryPageTitleCard
                title="SAC"
                componentItem={
                  <CustomButton
                    title="Novo atendimento"
                    onClick={() => {
                      navigate(routesConfig.INFORMATION().SAC_REQUEST);
                    }}
                    variant={
                      isAllowed
                        ? CustomButtonVariant.CONTAINED
                        : CustomButtonVariant.DISABLED
                    }
                    disabled={!isAllowed}
                  />
                }
              />
            </Grid>
            <Grid item xs={12}>
              <CollapsibleFilterCard
                title="Registros de atendimento"
                subtitle={getFormattedTimestamp(new Date())}
              >
                <SacFilter
                  dataFilter={(e) => {
                    handleDebounce(e);
                  }}
                />
              </CollapsibleFilterCard>
            </Grid>
          </Grid>
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CustomCard
            sx={{
              boxShadow: "none",
              border: "1px solid #DFE0EB",
              padding: 2.4,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CustomTabs
              indexTab={currentTab}
              tabsHeader={tabsLabel}
              onChangeTab={(idx) => {
                setCurrentTab(idx as number);
              }}
              body={[
                <OpenCallsTab dataFilter={query} key={0} />,
                <AwaitingConfirmationCallsTab dataFilter={query} key={1} />,
                <ClosedCallsTab dataFilter={query} key={2} />,
              ]}
            />
          </CustomCard>
        </Box>
      </Box>
    </Layout>
  );
};

export default SacPage;
